import React, { useState } from "react";
import { AppRole } from "@microsoft/microsoft-graph-types";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  TextField,
  Button,
  Stack,
  Box,
  Tooltip
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import SecurityIcon from "@mui/icons-material/Security";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import Chip from "@mui/material/Chip";
import PeopleIcon from "@mui/icons-material/People";
import ComputerIcon from "@mui/icons-material/Computer";
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Switch from '@mui/material/Switch';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { styled } from '@mui/material/styles';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

interface AppRolesProps {
  appRoles: AppRole[];
  setAppRoles: React.Dispatch<React.SetStateAction<AppRole[]>>;
  applicationId: string;
  patchApplicationRoles: (updatedRoles: AppRole[]) => Promise<void>;
  updateApplication?: () => Promise<void>;
}

const AppRoles: React.FC<AppRolesProps> = ({
  appRoles,
  setAppRoles,
  applicationId,
  patchApplicationRoles,
  updateApplication
}) => {
  // Accordion state
  const [rolesExpanded, setRolesExpanded] = useState(true);
  const [newRoleExpanded, setNewRoleExpanded] = useState(false);
  
  // New role form state
  const [newAppRoleId, setNewAppRoleId] = useState("");
  const [newAppRoleValue, setNewAppRoleValue] = useState("");
  const [newAppRoleDisplayName, setNewAppRoleDisplayName] = useState("");
  const [newAppRoleDescription, setNewAppRoleDescription] = useState("");
  
  // Alert states
  const [showAddReminder, setShowAddReminder] = useState(false);
  const [showRemoveReminder, setShowRemoveReminder] = useState(false);
  const [removedRoleName, setRemovedRoleName] = useState("");

  const [memberTypeSelection, setMemberTypeSelection] = useState<string>("users");

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentEditRole, setCurrentEditRole] = useState<AppRole | null>(null);
  const [editedRoleValue, setEditedRoleValue] = useState("");
  const [editedRoleDisplayName, setEditedRoleDisplayName] = useState("");
  const [editedRoleDescription, setEditedRoleDescription] = useState("");
  const [editedMemberTypeSelection, setEditedMemberTypeSelection] = useState<string>("users");
  const [showEditReminder, setShowEditReminder] = useState(false);
  const [editedRoleName, setEditedRoleName] = useState("");

 // Enterprise scale helper: Suggests role values based on display name patterns
  const suggestRoleValue = (displayName: string): string => {
    if (!displayName) return '';
    
    // Convert display name to appropriate format
    const words = displayName.split(/\s+/);
    if (words.length === 0) return '';
    
    // Common patterns in Azure app roles following Microsoft best practices
    const commonActions = ['Read', 'Write', 'Upload', 'Download', 'Admin', 'User', 'Manager', 'Editor', 'Viewer', 'Contributor'];
    
    for (const action of commonActions) {
      if (displayName.toLowerCase().includes(action.toLowerCase())) {
        // If a common action word is found in the display name
        const resourceName = words
          .filter(w => w.toLowerCase() !== action.toLowerCase())
          .join('');
        
        if (resourceName) {
          return `${resourceName}.${action}`;
        }
      }
    }
    
    // Default pattern: Resource.User following Azure naming conventions
    return `${words[0]}.User`;
  }; 

  const handleDisplayNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDisplayName = e.target.value;
    setNewAppRoleDisplayName(newDisplayName);
    
    // Only suggest if the value field is empty or hasn't been manually edited
    if (!newAppRoleValue || newAppRoleValue === suggestRoleValue(newAppRoleDisplayName)) {
      setNewAppRoleValue(suggestRoleValue(newDisplayName));
    }
  };

  const handleRolesAccordionChange = (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setRolesExpanded(isExpanded);
  };
  
  const handleNewRoleAccordionChange = (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setNewRoleExpanded(isExpanded);
  };

  const handleRemoveAppRole = async (roleId: string, roleName: string) => {
    // Prompt user for confirmation
    const confirmed = window.confirm(
      "Are you sure you want to delete this App Role?\n\n" +
      "Deleting roles can break user provisioning or other features!"
    );
    if (!confirmed) {
      return;
    }
  
    try {
      // Step 1: disable the role (but don't patch yet, just update local state)
      let rolesCopy = [...appRoles];
      const idx = rolesCopy.findIndex((r) => r.id === roleId);
      if (idx === -1) return;
  
      rolesCopy[idx] = { ...rolesCopy[idx], isEnabled: false };
      
      // Step 2: remove from the array (still just updating local state)
      rolesCopy.splice(idx, 1);
      setAppRoles(rolesCopy);
      
      // Show reminder to apply changes
      setRemovedRoleName(roleName);
      setShowRemoveReminder(true);
      
      // Auto-hide after 6 seconds
      setTimeout(() => {
        setShowRemoveReminder(false);
      }, 6000);
    } catch (err: any) {
      console.error("Failed to remove role:", err);
      alert("Error removing this role:\n" + (err.message || err.toString()));
    }
  };  

  const handleAddAppRole = () => {
    const roleId = newAppRoleId || crypto.randomUUID();
    if (!newAppRoleValue || !newAppRoleDisplayName) {
      alert("Role Value and Display Name are required.");
      return;
    }
  
    // Determine allowed member types based on selection
    let allowedMemberTypes: string[] = [];
    switch (memberTypeSelection) {
      case "users":
        allowedMemberTypes = ["User"];
        break;
      case "applications":
        allowedMemberTypes = ["Application"];
        break;
      case "both":
        allowedMemberTypes = ["User", "Application"];
        break;
      default:
        allowedMemberTypes = ["User"]; // Default fallback
    }
  
    const newRole: AppRole = {
      id: roleId,
      value: newAppRoleValue,
      displayName: newAppRoleDisplayName,
      description: newAppRoleDescription,
      allowedMemberTypes: allowedMemberTypes, // Use the dynamically determined types
      isEnabled: true
    };
  
    // Update local state only - don't patch until Apply Changes
    const newRoles = [...appRoles, newRole];
    setAppRoles(newRoles);
  
    // Clear input fields
    setNewAppRoleId("");
    setNewAppRoleValue("");
    setNewAppRoleDisplayName("");
    setNewAppRoleDescription("");
    // Reset member type to default
    setMemberTypeSelection("users");
    
    // Show reminder to apply changes
    setShowAddReminder(true);
    
    // Auto-hide after 6 seconds
    setTimeout(() => {
      setShowAddReminder(false);
    }, 6000);
  };

  // Add these helper functions for role editing
  const openEditDialog = (role: AppRole) => {
    setCurrentEditRole(role);
    setEditedRoleValue(role.value || "");
    setEditedRoleDisplayName(role.displayName || "");
    setEditedRoleDescription(role.description || "");
    
    // Set member type selection based on allowed member types
    if (role.allowedMemberTypes?.includes("User") && role.allowedMemberTypes?.includes("Application")) {
      setEditedMemberTypeSelection("both");
    } else if (role.allowedMemberTypes?.includes("Application")) {
      setEditedMemberTypeSelection("applications");
    } else {
      setEditedMemberTypeSelection("users");
    }
    
    setEditDialogOpen(true);
  };

  const closeEditDialog = () => {
    setEditDialogOpen(false);
    setCurrentEditRole(null);
  };

  // Add this function to handle the enable/disable toggle
  const handleToggleRoleEnabled = (roleId: string, currentState: boolean) => {
    // Update the role's isEnabled property
    const rolesCopy = [...appRoles];
    const idx = rolesCopy.findIndex((r) => r.id === roleId);
    if (idx === -1) return;
    
    rolesCopy[idx] = { ...rolesCopy[idx], isEnabled: !currentState };
    setAppRoles(rolesCopy);
    
    // Show reminder to apply changes
    setShowEditReminder(true);
    setEditedRoleName(rolesCopy[idx].displayName || "");
    
    // Auto-hide after 6 seconds
    setTimeout(() => {
      setShowEditReminder(false);
    }, 6000);
  };

  // Add this function to handle saving edited role
  const handleSaveEditedRole = () => {
    if (!currentEditRole || !editedRoleValue || !editedRoleDisplayName) {
      alert("Role Value and Display Name are required.");
      return;
    }
    
    // Determine allowed member types based on selection
    let allowedMemberTypes: string[] = [];
    switch (editedMemberTypeSelection) {
      case "users":
        allowedMemberTypes = ["User"];
        break;
      case "applications":
        allowedMemberTypes = ["Application"];
        break;
      case "both":
        allowedMemberTypes = ["User", "Application"];
        break;
      default:
        allowedMemberTypes = ["User"]; // Default fallback
    }
    
    // Update the role
    const rolesCopy = [...appRoles];
    const idx = rolesCopy.findIndex((r) => r.id === currentEditRole.id);
    if (idx === -1) return;
    
    rolesCopy[idx] = {
      ...rolesCopy[idx],
      value: editedRoleValue,
      displayName: editedRoleDisplayName,
      description: editedRoleDescription,
      allowedMemberTypes: allowedMemberTypes
    };
    
    setAppRoles(rolesCopy);
    closeEditDialog();
    
    // Show reminder to apply changes
    setShowEditReminder(true);
    setEditedRoleName(editedRoleDisplayName);
    
    // Auto-hide after 6 seconds
    setTimeout(() => {
      setShowEditReminder(false);
    }, 6000);
  };

  // Apply all changes at once (for the Apply Changes button)
  const handleApplyChanges = async () => {
    try {
      await patchApplicationRoles(appRoles);
      // Clear alerts
      setShowAddReminder(false);
      setShowRemoveReminder(false);
      
      // If parent also wants to do something
      if (updateApplication) {
        await updateApplication();
      }
    } catch (err: any) {
      console.error("Failed to apply changes:", err);
      alert("Error applying changes:\n" + (err.message || err.toString()));
    }
  };

  return (
    <Card variant="outlined" sx={{ mb: 4 }}>
      <CardContent>
        {/* Title */}
        {/* <Typography 
          variant="h6" 
          sx={{ 
            fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
            fontSize: "1.2rem",
            fontWeight: 600,
            color: "primary.main",
            letterSpacing: "0.01em",
            mb: 2
          }}
        >
          App Roles Configuration
        </Typography> */}
        
        {/* 1) CONFIGURED APP ROLES */}
        <Accordion
          expanded={rolesExpanded}
          onChange={handleRolesAccordionChange}
          sx={{
            border: '1px solid #e8f5e9', // Green border like OptionalClaims
            borderRadius: '12px !important', // Rounded corners like OptionalClaims
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)', // Same shadow as OptionalClaims
            mb: 2
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              background: 'linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)', // Green gradient like OptionalClaims
              borderBottom: rolesExpanded ? '1px solid #c8e6c9' : 'none',
              minHeight: '56px' // Same height as OptionalClaims
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{
                width: 28, // Same size as OptionalClaims
                height: 28,
                borderRadius: '8px', // Rounded square like OptionalClaims
                backgroundColor: '#4caf50', // Green background
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1.5
              }}>
                <SecurityIcon sx={{ color: 'white', fontSize: '0.85rem' }} /> {/* Use SecurityIcon for roles */}
              </Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                  fontSize: "1.2rem", // Same as OptionalClaims
                  fontWeight: 600,
                  color: "#2e7d32", // Green text color
                  letterSpacing: "0.01em"
                }}
              >
                Configured App Roles
              </Typography>
              {/* Add count badge like OptionalClaims */}
              <Chip 
                size="small"
                label={`${appRoles.length} roles`}
                sx={{ 
                  ml: 2,
                  backgroundColor: '#c8e6c9', // Light green background
                  color: '#2e7d32', // Dark green text
                  fontWeight: 500
                }}
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              backgroundColor: "#fafffe", // Light green background like OptionalClaims
              borderRadius: 1
            }}
          >
            <Box sx={{ p: 2 }}>
              {/* Remove Alert */}
              {showRemoveReminder && (
                <Box sx={{ 
                  p: 1.5, 
                  mb: 2,
                  backgroundColor: '#fdf5f5', 
                  borderLeft: '4px solid #f44336',
                  borderRadius: '0 4px 4px 0',
                  display: 'flex',
                  alignItems: 'center',
                  animation: 'fadeIn 0.3s ease-in-out'
                }}>
                  <Box sx={{ mr: 1.5, color: '#d32f2f' }}>
                    <InfoOutlinedIcon />
                  </Box>
                  <Typography variant="body2">
                    <strong>"{removedRoleName}" role removed!</strong> Remember to click <strong>Apply Changes</strong> to finalize this removal.
                  </Typography>
                </Box>
              )}

              {/* Edit Reminder Alert - ADD THIS */}
              {showEditReminder && (
                <Box sx={{ 
                  p: 1.5, 
                  mb: 2,
                  backgroundColor: '#e3f2fd', 
                  borderLeft: '4px solid #2196f3',
                  borderRadius: '0 4px 4px 0',
                  display: 'flex',
                  alignItems: 'center',
                  animation: 'fadeIn 0.3s ease-in-out'
                }}>
                  <Box sx={{ mr: 1.5, color: '#1976d2' }}>
                    <InfoOutlinedIcon />
                  </Box>
                  <Typography variant="body2">
                    <strong>"{editedRoleName}" role updated!</strong> Remember to click <strong>Apply Changes</strong> to save your modifications.
                  </Typography>
                </Box>
              )}
            
              {appRoles.length === 0 ? (
                <Box sx={{ 
                  p: 2, 
                  backgroundColor: '#f5f5f5', 
                  borderRadius: 1, 
                  textAlign: 'center',
                  border: '1px dashed #ccc'
                }}>
                  <Typography variant="body1" color="text.secondary">
                    No app roles configured yet.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Use the "New App Role" section below to add roles.
                  </Typography>
                </Box>
              ) : (
                <List>
                  {appRoles.map((role) => (
                    <ListItem
                      key={role.id}
                      secondaryAction={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Tooltip title={role.isEnabled !== false ? "Disable role" : "Enable role"}>
                            <IconButton
                              edge="end"
                              color={role.isEnabled !== false ? "default" : "warning"}
                              onClick={() => handleToggleRoleEnabled(role.id!, role.isEnabled !== false)}
                              sx={{ mr: 1 }}
                            >
                              {role.isEnabled !== false ? <VisibilityIcon /> : <VisibilityOffIcon />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit role">
                            <IconButton
                              edge="end"
                              color="primary"
                              onClick={() => openEditDialog(role)}
                              sx={{ mr: 1 }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete role">
                            <IconButton
                              edge="end"
                              color="error"
                              onClick={() => handleRemoveAppRole(role.id!, role.displayName!)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      }
                      sx={{ 
                        border: "1px solid #e0e0e0", 
                        borderRadius: 1, 
                        mb: 1,
                        backgroundColor: role.isEnabled === false ? "#f5f5f5" : "#ffffff",
                        opacity: role.isEnabled === false ? 0.8 : 1
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography 
                              variant="subtitle1" 
                              sx={{ 
                                textDecoration: role.isEnabled === false ? 'line-through' : 'none',
                                color: role.isEnabled === false ? 'text.secondary' : 'text.primary'
                              }}
                            >
                              <strong>{role.displayName}</strong>
                              {role.isEnabled === false && (
                                <Chip 
                                  label="Disabled" 
                                  size="small" 
                                  color="default"
                                  variant="outlined"
                                  sx={{ ml: 1, fontSize: '0.7rem' }}
                                />
                              )}
                            </Typography>
                            <Box>
                              {role.allowedMemberTypes?.includes("User") && (
                                <Chip 
                                  icon={<PeopleIcon />} 
                                  label="Users/Groups" 
                                  size="small" 
                                  sx={{ fontSize: '0.75rem', mr: 0.5 }} 
                                  color="primary"
                                  variant="outlined"
                                />
                              )}
                              {role.allowedMemberTypes?.includes("Application") && (
                                <Chip 
                                  icon={<ComputerIcon />} 
                                  label="Applications" 
                                  size="small" 
                                  sx={{ fontSize: '0.75rem' }} 
                                  color="secondary"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography 
                              variant="body2" 
                              component="span"
                              sx={{ color: role.isEnabled === false ? 'text.disabled' : 'text.secondary' }}
                            >
                              <strong>Value:</strong> {role.value}
                            </Typography>
                            <br />
                            {role.description && (
                              <>
                                <Typography 
                                  variant="body2" 
                                  component="span"
                                  sx={{ color: role.isEnabled === false ? 'text.disabled' : 'text.secondary' }}
                                >
                                  <strong>Description:</strong> {role.description}
                                </Typography>
                                <br />
                              </>
                            )}
                            <Typography 
                              variant="caption" 
                              sx={{ color: role.isEnabled === false ? 'text.disabled' : 'text.secondary' }}
                            >
                              <strong>ID:</strong> {role.id}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>                    
                  ))}
                </List>
              )}
              
              {/* Apply Changes Button for Removal */}
              {(showRemoveReminder || showEditReminder || showAddReminder) && (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'flex-end', 
                  mt: 2, 
                  pt: 1, 
                  borderTop: '1px solid #e0e0e0' 
                }}>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<SaveIcon />}
                    onClick={handleApplyChanges}
                  >
                    Apply Changes
                  </Button>
                </Box>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* 2) NEW APP ROLE */}
        <Accordion
          expanded={newRoleExpanded}
          onChange={handleNewRoleAccordionChange}
          sx={{
            border: '1px solid #e3f2fd', // Blue border like OptionalClaims
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
            mb: 2
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              background: 'linear-gradient(135deg, #e3f2fd 0%, #f0f8ff 100%)', // Blue gradient like OptionalClaims
              borderBottom: newRoleExpanded ? '1px solid #bbdefb' : 'none',
              minHeight: '56px'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{
                width: 28,
                height: 28,
                borderRadius: '8px',
                backgroundColor: '#2196f3', // Blue background
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1.5
              }}>
                <AddIcon sx={{ color: 'white', fontSize: '1rem' }} />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                  fontSize: "1.2rem", // Same as OptionalClaims
                  fontWeight: 600,
                  color: "#1565c0", // Blue text color
                  letterSpacing: "0.01em"
                }}
              >
                New App Role
              </Typography>
              <Chip 
                size="small"
                label="Configure" 
                sx={{ 
                  ml: 2,
                  backgroundColor: '#bbdefb', // Light blue background
                  color: '#1565c0', // Dark blue text
                  fontWeight: 500
                }}
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              backgroundColor: "#f8fbff", // Light blue background like OptionalClaims
              borderRadius: 1
            }}
          >
            <Box sx={{ p: 2 }}>
              {/* Add Reminder Alert */}
              {showAddReminder && (
                <Box sx={{ 
                  p: 1.5, 
                  mb: 2,
                  backgroundColor: '#fff3e0', 
                  borderLeft: '4px solid #ff9800',
                  borderRadius: '0 4px 4px 0',
                  display: 'flex',
                  alignItems: 'center',
                  animation: 'fadeIn 0.3s ease-in-out'
                }}>
                  <Box sx={{ mr: 1.5, color: '#f57c00' }}>
                    <InfoOutlinedIcon />
                  </Box>
                  <Typography variant="body2">
                    <strong>App role added!</strong> Remember to click <strong>Apply Changes</strong> to save your modifications.
                  </Typography>
                </Box>
              )}
            
              <TextField
                label="Role ID"
                value={newAppRoleId}
                onChange={(e) => setNewAppRoleId(e.target.value)}
                sx={{ mb: 1 }}
                fullWidth
                placeholder="Enter a UUID or leave blank to auto-generate"
                helperText="If empty, a random UUID will be used."
              />
              <TextField
                label="Role Value"
                value={newAppRoleValue}
                onChange={(e) => setNewAppRoleValue(e.target.value)}
                sx={{ mb: 1 }}
                fullWidth
                placeholder="e.g. Task.Read or Report.Write"
                helperText="The 'Value' is the permission string in tokens."
              />
              <TextField
                label="Role Display Name"
                value={newAppRoleDisplayName}
                onChange={handleDisplayNameChange} // Use the new handler instead of direct state setter
                sx={{ mb: 1 }}
                fullWidth
                placeholder="e.g. 'Readers' or 'Writers'"
                helperText="Visible name of the role in Entra. Role Value will be suggested automatically."
              />
              <TextField
                label="Role Description"
                value={newAppRoleDescription}
                onChange={(e) => setNewAppRoleDescription(e.target.value)}
                sx={{ mb: 2 }}
                fullWidth
                placeholder="Short description, e.g. 'Can read tasks.'"
              />

              <FormControl component="fieldset" sx={{ mb: 2 }}>
                <FormLabel component="legend" sx={{ fontWeight: 500 }}>
                  Allowed Member Types
                </FormLabel>
                <RadioGroup
                  value={memberTypeSelection}
                  onChange={(e) => setMemberTypeSelection(e.target.value)}
                  row
                >
                  <FormControlLabel 
                    value="users" 
                    control={<Radio />} 
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PeopleIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
                        Users/Groups
                      </Box>
                    } 
                  />
                  <FormControlLabel 
                    value="applications" 
                    control={<Radio />} 
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ComputerIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
                        Applications
                      </Box>
                    } 
                  />
                  <FormControlLabel 
                    value="both" 
                    control={<Radio />} 
                    label="Both" 
                  />
                </RadioGroup>
                <Typography variant="caption" color="text.secondary">
                  Select which types of members can be assigned this app role
                </Typography>
              </FormControl>              

              {/* Action buttons with improved styling */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                mt: 1, 
                pt: 2, 
                borderTop: '1px solid #e0e0e0' 
              }}>
                <Button 
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddAppRole}
                  disabled={!newAppRoleValue || !newAppRoleDisplayName}
                >
                  Add App Role
                </Button>
                
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<SaveIcon />}
                  onClick={handleApplyChanges}
                >
                  Apply Changes
                </Button>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
        
        {/* 3) ABOUT APP ROLES - Information Accordion */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography 
              variant="subtitle1"
              sx={{ 
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <InfoOutlinedIcon sx={{ mr: 1, fontSize: "1.2rem", color: 'info.main' }} />
              About App Roles
            </Typography>
          </AccordionSummary>

          <AccordionDetails>
            <Box sx={{ p: 2, backgroundColor: "#f5f5ff", borderRadius: 1 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                App roles define permissions that can be assigned to users, applications, or service principals that need to access your application.
              </Typography>
              
              {/* Visual explanation of app roles */}
              <Box sx={{ 
                p: 2, 
                backgroundColor: '#ffffff', 
                border: '1px solid #e0e0ff', 
                borderRadius: 1,
                mb: 2
              }}>
                <Typography variant="subtitle2" sx={{ color: '#0d47a1', mb: 1 }}>
                  App Roles Components:
                </Typography>
                <Typography variant="body2">
                  • <strong>Display Name:</strong> The name of the role shown in the Azure portal<br />
                  • <strong>Value:</strong> The string value included in the roles claim in tokens<br />
                  • <strong>Description:</strong> Details about what the role permits<br />
                  • <strong>Allowed Member Types:</strong> Which types of identities can be assigned this role<br />
                  • <strong>ID:</strong> A unique identifier (UUID) for the role
                </Typography>
              </Box>
              
              <Box sx={{
                p: 2,
                backgroundColor: '#fff8e1',
                border: '1px solid #ffe082',
                borderRadius: 1
              }}>
                <Typography variant="subtitle2" sx={{ color: '#bf360c', mb: 1 }}>
                  Important Considerations:
                </Typography>
                <Typography variant="body2">
                  • App roles are included in access tokens when users or apps authenticate to your application<br />
                  • App roles can be assigned through the AppConfig², Azure portal, PowerShell, or Microsoft Graph<br />
                  • Deleting an app role doesn't automatically remove assignments to that role<br />
                  • The role value is case-sensitive and should follow a consistent pattern (e.g., "Task.Read")<br />
                  • Enterprise applications check the roles claim to enforce authorization
                </Typography>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Edit Role Dialog */}
        <Dialog 
          open={editDialogOpen} 
          onClose={closeEditDialog}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle sx={{ 
            bgcolor: 'primary.main', 
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <EditIcon sx={{ mr: 1 }} />
              Edit App Role
            </Box>
            <IconButton 
              onClick={closeEditDialog}
              sx={{ color: 'white' }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          
          <DialogContent sx={{ p: 3, mt: 1 }}>
            <Box sx={{ mb: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: 1, border: '1px solid #eaecef' }}>
              <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center' }}>
                <HelpOutlineIcon sx={{ fontSize: '1rem', mr: 0.5, color: 'info.main' }} />
                You're editing an existing app role. The ID cannot be changed, but you can modify other properties.
              </Typography>
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={600}>Role ID (read-only):</Typography>
              <Typography variant="body2" color="text.secondary">{currentEditRole?.id}</Typography>
            </Box>
            
            <TextField
              label="Role Value"
              value={editedRoleValue}
              onChange={(e) => setEditedRoleValue(e.target.value)}
              sx={{ mb: 2 }}
              fullWidth
              required
              helperText={
                <Typography variant="caption" color="warning.main">
                  <b>Warning:</b> Changing this value may break existing applications using this role.
                </Typography>
              }
            />
            
            <TextField
              label="Role Display Name"
              value={editedRoleDisplayName}
              onChange={(e) => setEditedRoleDisplayName(e.target.value)}
              sx={{ mb: 2 }}
              fullWidth
              required
            />
            
            <TextField
              label="Role Description"
              value={editedRoleDescription}
              onChange={(e) => setEditedRoleDescription(e.target.value)}
              sx={{ mb: 3 }}
              fullWidth
              multiline
              rows={2}
            />
            
            <FormControl component="fieldset" sx={{ mb: 0 }}>
              <FormLabel component="legend" sx={{ fontWeight: 500 }}>
                Allowed Member Types
              </FormLabel>
              <RadioGroup
                value={editedMemberTypeSelection}
                onChange={(e) => setEditedMemberTypeSelection(e.target.value)}
                row
              >
                <FormControlLabel 
                  value="users" 
                  control={<Radio />} 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PeopleIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
                      Users/Groups
                    </Box>
                  } 
                />
                <FormControlLabel 
                  value="applications" 
                  control={<Radio />} 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ComputerIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
                      Applications
                    </Box>
                  } 
                />
                <FormControlLabel 
                  value="both" 
                  control={<Radio />} 
                  label="Both" 
                />
              </RadioGroup>
              <Typography variant="caption" color="warning.main">
                <b>Warning:</b> Changing member types may affect existing role assignments.
              </Typography>
            </FormControl>
          </DialogContent>
          
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={closeEditDialog} color="inherit" variant="outlined">
              Cancel
            </Button>
            <Button 
              onClick={handleSaveEditedRole} 
              color="primary" 
              variant="contained"
              disabled={!editedRoleValue || !editedRoleDisplayName}
            >
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

      </CardContent>
    </Card>
  );
};

export default AppRoles;